import React, {useRef, useState} from "react";
import {
	Alert, FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment, InputLabel, LinearProgress, MenuItem,
	Radio,
	RadioGroup, Select,
	Stack,
	Switch,
	TextField
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {DocumentMeta} from "../../models/DocumentMeta";
import {DocumentValidation} from "../../models/DocumentValidation";
import {ValidationSeverity} from "../../models/ValidationSeverity";
import {MetaValueType} from "../../models/MetaValueType";
import {DatePicker} from "../../../../components/common/input/DatePicker";
import {LocalDate} from "@js-joda/core";
import {GlobalState} from "../../../../global/GlobalState";
import {translateFunction} from "../../../../localization/TranslationService";
import {KeyValue} from "../../models/KeyValue";

const translate = translateFunction("SureBaseDocuments");
interface IMetaSelectorProps {
	hideInput?: boolean ;
	hideIfEmpty?: boolean;
	field?: string;
	label: string;
	metaKey: string;
	metaType: MetaValueType;
	search: boolean;
	metaData: DocumentMeta[]|null;
	validations: DocumentValidation[]|null;
	onChange?: (key: string, field?: string, value?: string) => Promise<boolean>;
	onSearch?: (field: string, value?: string, searchClicked?: boolean) => void;
	inputKey?: string;
	readOnly?: boolean;
	disableFuture?: boolean;
	disablePast?: boolean;
	values?: KeyValue[];
	endAdornment?: JSX.Element;
}

const MetaSelectorComponent: React.FC<IMetaSelectorProps> = (props) => {
	const textInput = useRef<HTMLInputElement>(null);
	const newValue = useRef<string>();
	const metaData = props.metaData?.filter((it) => it.key === props.metaKey) ?? [];
	const metaValue = (metaData.length === 1 ? (metaData.firstOrNull()?.value ?? "") : "");
	const [value, setValue] = useState<string>(metaValue);
	
	const validations = props.validations?.filter(
		(it) => it.field === props.metaKey
	);
	
	if (metaValue === "" && props.hideIfEmpty)
		return null;
	return (
		<>
			<Stack spacing={1}>
				{validations && validations.length > 0 && (
					<Stack>
						{validations?.map((it) => {
							return (
								<Alert key={it.id}
									severity={it.severity === ValidationSeverity.ERROR ? "error" : "warning"}>{translate(it.message)}</Alert>
							);
						})}
					</Stack>
				)}
				{!props.hideInput &&
					<>
						{props.metaType === MetaValueType.String &&
							<TextField
								label={props.label}
								inputRef={textInput}
								placeholder={metaData && metaData.length > 1 ? "Selecteer of type" : (props.readOnly ? "" : "Type")}
								id={"meta-edit-" + props.metaKey}
								key={props.inputKey ?? props.metaKey}
								onBlur={async () => {
									if (props.onChange && value !== (textInput.current?.value ?? "")) {
										
										newValue.current = textInput.current?.value ?? "";
										if (!await props.onChange(props.metaKey, props.field, newValue.current)) {
											props.onSearch && props.field && props.onSearch(props.field, newValue.current, false);
										}
										//setValue(textInput.current?.value ?? "");
									} else if (value !== (textInput.current?.value ?? "")) {
										props.onSearch && props.field && props.onSearch(props.field, textInput.current?.value, false);
									}
								}
								}
								defaultValue={value ?? metaValue}
								InputProps={{
									readOnly: props.readOnly,
									style: {backgroundColor: (props.readOnly && !props.search) ? "lightgray" : "white"},
									endAdornment:
										<InputAdornment position="end">{props.search && !props.readOnly &&
											<IconButton
												onClick={() => props.onSearch && props.field && props.onSearch(props.field, textInput.current?.value, true)}>
												<SearchIcon></SearchIcon></IconButton>
											
										}
										{props.endAdornment}
										</InputAdornment>
								}}
							
							/>}
						{props.metaType === MetaValueType.Boolean &&
							<Stack direction={"row"}>
								<Switch
									inputRef={textInput}
									readOnly={props.readOnly}
									disabled={props.readOnly}
									checked={(value ?? metaValue) === "true"}
									onChange={() => {
										if (props.onChange) {
											props.onChange(props.metaKey, props.field, (textInput.current?.checked ? "true" : "false"));
										}
									}
									}/>
								<div style={{display: "flex", alignItems: "center"}}>{props.label}</div>
							</Stack>
						}
						{props.metaType === MetaValueType.Date &&
							<DatePicker
								disableFuture={props.disableFuture}
								disablePast={props.disablePast}
								label={props.label}
								value={(value) ? LocalDate.parse(value) : undefined}
								disabled={props.readOnly}
								onValueChange={(value) => {
									if (props.onChange) {
										props.onChange(props.metaKey, props.field, value?.toString());
									}
								}}
								inputFormat={GlobalState.userPreferences.datePatternOrDefault}/>
						}
						
						{metaData && metaData.length === 1 && props.metaType === MetaValueType.String &&
							<LinearProgress variant="buffer" color={"success"} value={metaData.first().confidence}
								valueBuffer={metaData.first().confidence}/>
						}
					
					</>}
				{props.metaType === MetaValueType.RadioButtons &&
					<>
						<RadioGroup
							aria-labelledby="radio-buttons-group-label"
							id={"meta-edit-" + props.metaKey}
							key={props.inputKey ?? props.metaKey}
							defaultValue={value ?? metaValue}
							row
							onChange={async (event) => {
								setValue(textInput.current?.value ?? "");
								if (props.onChange && !await props.onChange(props.metaKey, props.field, event.target.value)) {
									props.onSearch && props.field && props.onSearch(props.field, event.target.value);
								}
							}}
						>
							{props.values && props.values.length > 1 &&
								props.values.map((it) => {
									return (
										<FormControlLabel key={it.key} value={it.key} control={<Radio/>}
											label={it.value}/>
									);
								})
							}
						</RadioGroup>
					</>
				}
				
				{props.metaType === MetaValueType.DropDown &&
					<>
						<FormControl fullWidth>
							<InputLabel id={"meta-edit-label-" + props.metaKey}>{props.label}</InputLabel>
							<Select
								defaultValue={value ?? metaValue}
								id={"meta-edit-" + props.metaKey}
								key={props.inputKey ?? props.metaKey}
							>
								{props.values && props.values.length > 1 &&
									props.values.map((it) => {
										return (
											<MenuItem value={it.value}>{it.key}</MenuItem>
										);
									})
								}
							</Select>
						</FormControl>
					</>
				}
				{<RadioGroup
					aria-labelledby="radio-buttons-group-label"
					name="radio-buttons-group"
					aria-readonly={props.readOnly}
					onChange={async (event) => {
						setValue(textInput.current?.value ?? "");
						if (props.onChange && !await props.onChange(props.metaKey, props.field, event.target.value)) {
							props.onSearch && props.field && props.onSearch(props.field, event.target.value);
						}
						
					}}
				>
					{metaData && metaData.length > 1 &&
						metaData.map((it) => {
							return (
								<FormControlLabel key={it.id} value={it.value} control={<Radio/>} label={it.value}/>
							);
						})
					}
				</RadioGroup>}
			</Stack>
		</>
	);
	
};

export const MetaSelector = React.memo(MetaSelectorComponent);
